import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { Redirect, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { PaymentBank } from "./Components/PaymentBank";
import { PayrunCycle } from "./Components/PayrunCycle";
import { PayrollGroup } from "./Components/PayrollGroup";
import { GeneralConfiguration } from "./Components/GeneralConfiguration";
import { PaySlip } from "./Components/PaySlip";
import { DailyCalculationBasis } from "./Components/DailyCalculationBasis";
import { theme } from "services/constants";
import { useSelector, useDispatch } from "react-redux";
import { FilterProvider } from "pages/FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { designColors } from "services/constants/designTokens";
import { PAYROLL_HOME_PATH, usePayrollSettingsCrumbs } from "../payrollSettingsMenu";
import { useHoistedBreadcrumb } from "pages/PayRoll/Shared/settingsBreadcrumb";

// Payroll Settings > Payroll. Its items are rows under Payroll in the
// Payroll Settings menu (payrollSettingsMenu.js). Each item has its own
// link: PAYROLL_SETUP_PATH?view=<slug>.
export const PAYROLL_SETUP_PATH =
  "/admin/payroll/configuration/payroll-configuration";
const PAYROLL_SETUP_ITEMS = [
  { view: "payment-bank", key: "Payment Bank" },
  { view: "payrun-cycle", key: "Payrun Cycle" },
  { view: "payroll-group", key: "Payroll Group" },
  { view: "rounding-rules", key: "Rounding Rules" },
  { view: "pay-slip", key: "Pay Slip" },
  { view: "daily-calculation-basis", key: "Daily Calculation Basis" },
];

export const PayrollSetUp = () => {
  const location = useLocation();
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const view = (new URLSearchParams(location.search).get("view") || "").toLowerCase();
  const selectedItem = PAYROLL_SETUP_ITEMS.find((item) => item.view === view);
  const selectPayrollSetupItem = selectedItem?.key;
  const breadcrumb = settingsCrumbs("Payroll", selectPayrollSetupItem || "");
  // Inside Payroll Settings the breadcrumb shows at the top of the screen.
  const breadcrumbHoisted = useHoistedBreadcrumb(breadcrumb);
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();

  // Switching item reloads the list screens' data, as the old in-page list
  // did on click (not on first load).
  const previousItem = useRef(selectPayrollSetupItem);
  useEffect(() => {
    if (previousItem.current && previousItem.current !== selectPayrollSetupItem) {
      dispatch(setFilterApi(true));
    }
    previousItem.current = selectPayrollSetupItem;
  }, [dispatch, selectPayrollSetupItem]);

  // The backend's real module_name for this screen is "Settings" (not
  // "Setup" and not "Configuration" — both were stale/wrong strings that
  // never matched anything in a live provisioning payload, so every
  // permission check here silently read as "not granted" regardless of the
  // user's actual access). Confirmed against a real Payroll provisioning
  // payload: "Settings"'s submodules include Payrun Cycle, Payment Bank and
  // Payroll Group, which match this screen's own tabs by that name.
  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Settings",
  );

  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === selectPayrollSetupItem,
  )?.sub_module_id;

  const renderMainContent = () => {
    switch (selectPayrollSetupItem) {
      case "Payrun Cycle":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <PayrunCycle  subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Payment Bank":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <PaymentBank  subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Payroll Group":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <PayrollGroup  subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Rounding Rules":
        return <GeneralConfiguration />;

      case "Pay Slip":
        return <PaySlip />;

      case "Daily Calculation Basis":
        return <DailyCalculationBasis />;

      default:
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <PayrunCycle />
          </FilterProvider>
        );
    }
  };

  // The old Payroll home (/admin/payroll/setup, still linked from some
  // breadcrumbs and bookmarks) opens Payroll's landing page,
  // Salary > Salary Components.
  if (location.pathname.replace(/\/+$/, "") === "/admin/payroll/setup") {
    return <Redirect to={PAYROLL_HOME_PATH} />;
  }
  // No valid ?view=: open the first item so the sidebar can highlight it.
  if (!selectedItem) {
    return <Redirect to={`${PAYROLL_SETUP_PATH}?view=${PAYROLL_SETUP_ITEMS[0].view}`} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "94.5vh",
          p: 3,
          minWidth: 0,
          backgroundColor: designColors.pageBg,
        }}
      >
        {breadcrumbHoisted ? null : (
          <GlobalBreadcrumb trail={breadcrumb} sx={{ mb: 2 }} />
        )}

        {renderMainContent()}
      </Box>
    </ThemeProvider>
  );
};
